import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  IconButton,
  Stack,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import CloseIcon from '@mui/icons-material/Close';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { RootState } from 'src/redux/rootReducerType';
import {
  setOpenHouseholdComposition,
  setOpenHouseholdCompositionForCertId,
  addHouseholdConfirmed,
  removeHouseholdConfirmed,
  setHouseholdCompositionConfirmed,
} from 'src/redux/slices/NewRenterInterface/NewRenterInterfaceSlice';
import axios from 'src/utils/axios';
import HouseholdCompositionPage, {
  HouseholdCompositionPageRef,
} from './HouseholdCompositionPage';
import { HELP_BOX_DISMISS_KEYS, HOUSEHOLD_STRUCTURE_STEPS } from '../constants';
import { InfoBox } from 'src/components/RenterSettings/help/HelpInfoBox';
import useHelpInfoBox from 'src/components/RenterSettings/help/useHelpInfoBox';
import HouseholdInfoBoxContent from '../Household/HouseholdInfoBoxContent';
import { getDuplicateContacts } from '../Household/householdContactUtils';

/**
 * Modal that manages multiple unconfirmed households.
 * Shows one household at a time with member list; after confirming, shows the next
 * until all are confirmed. Runs after sign-in, TOS accept, or page reload.
 */
const HouseholdCompositionModal: FC = () => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const location = useLocation();
  const householdCompositionHelpBox = useHelpInfoBox(
    HELP_BOX_DISMISS_KEYS.HOUSEHOLD_COMPOSITION
  );

  const open = useSelector(
    (state: RootState) => state.newRenterInterface.openHouseholdComposition
  );
  const openForCertId = useSelector(
    (state: RootState) =>
      state.newRenterInterface.openHouseholdCompositionForCertId
  );
  const unconfirmedHouseholds = useSelector(
    (state: RootState) => state.newRenterInterface.unconfirmedHouseholds
  );
  const householdsConfirmed = useSelector(
    (state: RootState) => state.newRenterInterface.householdsConfirmed
  );
  const householdsTotalCount = useSelector(
    (state: RootState) => state.newRenterInterface.householdsTotalCount
  );
  const householdsCurrentIndex = useSelector(
    (state: RootState) => state.newRenterInterface.householdsCurrentIndex
  );
  const householdsTransferred = useSelector(
    (state: RootState) => state.newRenterInterface.householdsTransferred
  );
  const certifications = useSelector(
    (state: RootState) => state.newRenterInterface.certifications
  );
  const userId = useSelector(
    (state: RootState) => state.newRenterInterfaceUser.user?.id
  );

  const [confirming, setConfirming] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentStep, setCurrentStep] = useState<HOUSEHOLD_STRUCTURE_STEPS>(
    HOUSEHOLD_STRUCTURE_STEPS.HOUSEHOLD_DISPLAY
  );

  const pageRef = useRef<HouseholdCompositionPageRef>(null);

  const handleBackToList = useCallback(() => {
    pageRef.current?.goBackToList();
  }, []);

  const forcedItem = useMemo(() => {
    if (!openForCertId) {
      return null;
    }
    const cert = certifications.find((c) => c.id === openForCertId);
    if (!cert?.cert_household) {
      return null;
    }
    return {
      household: cert.cert_household,
      certificationId: cert.id,
      property: cert.property ?? undefined,
    };
  }, [openForCertId, certifications]);

  // currentItem is the forced cert OR the first unconfirmed household
  const currentItem =
    forcedItem ?? unconfirmedHouseholds[householdsCurrentIndex] ?? null;
  const currentHousehold = currentItem?.household;
  const currentProperty = currentItem?.property;

  // Only managing cert members (HoH, Co-Head, Spouse, Helper) may add/edit
  // members or confirm composition. Non-managing members get a read-only view.
  const currentUserIsManaging = useMemo(
    () =>
      currentHousehold?.cert_members?.some(
        (m) => m.user_id === userId && m.is_managing
      ) ?? false,
    [currentHousehold, userId]
  );

  const duplicateContacts = useMemo(
    () => getDuplicateContacts(currentHousehold?.cert_members ?? []),
    [currentHousehold]
  );

  const memberNamesById = useMemo(() => {
    const map: Record<number, string> = {};
    for (const m of currentHousehold?.cert_members ?? []) {
      map[m.cert_member_id] =
        [m.first_name, m.middle_name, m.last_name]
          .filter(Boolean)
          .join(' ')
          .trim() || `Member ${m.cert_member_id}`;
    }
    return map;
  }, [currentHousehold]);

  useEffect(() => {
    const isRenterHome =
      location.pathname === '/renter' || location.pathname === '/renter/';
    if (
      !open &&
      !openForCertId &&
      unconfirmedHouseholds.length > 0 &&
      isRenterHome
    ) {
      dispatch(setOpenHouseholdComposition(true));
    }
  }, [
    open,
    openForCertId,
    unconfirmedHouseholds.length,
    dispatch,
    location.pathname,
  ]);

  const isImperativeMode = openForCertId !== null;
  const isModalOpen =
    (open || isImperativeMode) &&
    (currentItem !== null ||
      householdsConfirmed.length > 0 ||
      householdsTransferred.length > 0);

  // When the modal is opened via the "Edit" button (imperative mode), clear any
  // stale confirmed state for this cert. The WebSocket fires after the API call
  // and dispatches addHouseholdConfirmed — which would cause the success screen
  // to appear the next time the user opens this modal.
  useEffect(() => {
    if (
      isImperativeMode &&
      openForCertId !== null &&
      householdsConfirmed.includes(openForCertId)
    ) {
      dispatch(removeHouseholdConfirmed(openForCertId));
    }
  }, [isImperativeMode, openForCertId, householdsConfirmed, dispatch]);

  const handleImperativeClose = useCallback(() => {
    dispatch(setOpenHouseholdCompositionForCertId(null));
    dispatch(setOpenHouseholdComposition(false));
  }, [dispatch]);

  const handleStepChange = useCallback(
    (step: HOUSEHOLD_STRUCTURE_STEPS) => setCurrentStep(step),
    []
  );

  const handleConfirm = async (): Promise<void> => {
    if (!currentHousehold?.id) {
      return;
    }

    setError(null);
    setConfirming(true);

    try {
      await axios.post(
        `household/cert_household/${currentItem.household.id}/confirm_composition/`
      );

      const cert = certifications?.find(
        (c) => c.cert_household?.id === currentHousehold.id
      );
      if (cert && userId) {
        dispatch(
          setHouseholdCompositionConfirmed({
            certificationId: cert.id,
            userId,
          })
        );
        if (isImperativeMode) {
          // Opened via the "Edit" button — just close, no success screen needed.
          handleImperativeClose();
        } else {
          dispatch(addHouseholdConfirmed(cert.id));
        }
      }
    } catch {
      setError('Unable to confirm. Please try again.');
    } finally {
      setConfirming(false);
    }
  };

  const handleHelpToggle = (): void => {
    householdCompositionHelpBox.handleToggle();
  };

  const handleHelpDismiss = (): void => {
    householdCompositionHelpBox.handleDismiss();
  };

  const getTotalHouseholdCount = (): string => {
    if (householdsTotalCount < 2) {
      return '';
    }
    const completedCount = householdsTotalCount - unconfirmedHouseholds.length;
    return `(${completedCount + 1}/${householdsTotalCount})`;
  };

  const canConfirm = (): boolean => {
    return (
      currentStep === HOUSEHOLD_STRUCTURE_STEPS.HOUSEHOLD_DISPLAY &&
      currentUserIsManaging &&
      duplicateContacts.length === 0 &&
      householdsConfirmed.length === 0 &&
      householdsTransferred.length === 0
    );
  };

  if (!isModalOpen) {
    return null;
  }

  return (
    <Dialog
      open={isModalOpen}
      disableEscapeKeyDown={!isImperativeMode}
      fullWidth
      maxWidth="md"
      fullScreen={isMobile}
      slotProps={{
        paper: {
          sx: {
            margin: isMobile ? '0 !important' : 4,
            height: isMobile ? '100%' : '80dvh',
            backgroundColor:
              theme.palette.mode === 'dark'
                ? theme.palette.background.default
                : theme.palette.background.neutral,
          },
        },
      }}
    >
      {/* ── Green header ── */}
      <Box
        sx={{
          backgroundColor: 'primary.dark',
          px: 3,
          pt: 2.5,
          pb: 2,
          flexShrink: 0,
        }}
      >
        <Stack direction="row" alignItems="center" gap={1}>
          {/* Back arrow — only visible on sub-steps (Add / Edit) */}
          {currentStep !== HOUSEHOLD_STRUCTURE_STEPS.HOUSEHOLD_DISPLAY && (
            <IconButton
              size="small"
              onClick={handleBackToList}
              aria-label="Back to household list"
              sx={{
                backgroundColor: 'common.white',
                color: 'grey.900',
                p: 0.75,
                flexShrink: 0,
                '&:hover': { backgroundColor: 'grey.200' },
              }}
            >
              <ArrowBackIcon sx={{ fontSize: 18 }} />
            </IconButton>
          )}

          {/* Title */}
          <Typography
            variant="h6"
            fontWeight={700}
            sx={{ color: 'primary.contrastText', flex: 1 }}
          >
            {currentStep === HOUSEHOLD_STRUCTURE_STEPS.EDIT_MEMBER
              ? 'Edit Household Member'
              : currentStep === HOUSEHOLD_STRUCTURE_STEPS.ADD_MEMBER
              ? 'New Household Member'
              : `Household Composition ${getTotalHouseholdCount()}`}
          </Typography>

          {/* Help */}
          <Tooltip
            title="Click to see information about household composition"
            placement="bottom"
            arrow
            slotProps={{ tooltip: { sx: { maxWidth: 400, p: 2 } } }}
          >
            <IconButton onClick={handleHelpToggle} size="small">
              <HelpOutlineIcon
                sx={{
                  color: 'primary.contrastText',
                  fontSize: 20,
                  opacity: 0.8,
                }}
              />
            </IconButton>
          </Tooltip>

          {/* Close — imperative mode only */}
          {isImperativeMode && (
            <IconButton
              onClick={handleImperativeClose}
              size="small"
              aria-label="Close household composition"
            >
              <CloseIcon
                sx={{
                  color: 'primary.contrastText',
                  fontSize: 20,
                  opacity: 0.8,
                }}
              />
            </IconButton>
          )}
        </Stack>

        {/* Property name as subtitle */}
        {currentProperty &&
          !(householdsConfirmed.length || householdsTransferred.length) && (
            <Typography
              variant="body2"
              sx={{
                color: 'primary.contrastText',
                opacity: 0.75,
                mt: 0.5,
                ml:
                  currentStep !== HOUSEHOLD_STRUCTURE_STEPS.HOUSEHOLD_DISPLAY
                    ? 4.5
                    : 0,
              }}
            >
              {currentProperty.name}
            </Typography>
          )}
      </Box>

      <DialogContent>
        <InfoBox
          onDismiss={handleHelpDismiss}
          shouldShow={householdCompositionHelpBox.showInfo}
        >
          <HouseholdInfoBoxContent
            propertyName={currentProperty?.name}
            canManage={currentUserIsManaging}
          />
        </InfoBox>

        {currentStep === HOUSEHOLD_STRUCTURE_STEPS.HOUSEHOLD_DISPLAY &&
          currentUserIsManaging && (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mt: 1, mb: 2 }}
            >
              Household modifications will be reviewed before confirming
              eligibility.
            </Typography>
          )}

        <HouseholdCompositionPage
          ref={pageRef}
          property={currentProperty}
          onStepChange={handleStepChange}
        />

        {currentStep === HOUSEHOLD_STRUCTURE_STEPS.HOUSEHOLD_DISPLAY &&
          currentUserIsManaging &&
          duplicateContacts.length > 0 && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              <AlertTitle>Duplicate contact information</AlertTitle>
              {duplicateContacts.map((d) => {
                const names = d.cert_member_ids
                  .map((id) => memberNamesById[id] ?? `Member ${id}`)
                  .join(' and ');
                return (
                  <Typography key={`${d.type}-${d.value}`} variant="body2">
                    {d.type === 'email' ? 'Email' : 'Phone number'}{' '}
                    <strong>{d.value}</strong> is shared by {names}. Please
                    update the contact information before confirming.
                  </Typography>
                );
              })}
            </Alert>
          )}

        {error && (
          <DialogContentText color="error" sx={{ mt: 2 }}>
            {error}
          </DialogContentText>
        )}
      </DialogContent>

      {canConfirm() && (
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button
            onClick={handleConfirm}
            color="primary"
            variant="contained"
            size="large"
            disabled={confirming}
            fullWidth={isMobile}
            startIcon={
              confirming ? <CircularProgress size={20} color="inherit" /> : null
            }
          >
            <span>{confirming ? 'Confirming…' : 'Confirm Household'}</span>
          </Button>
        </DialogActions>
      )}
    </Dialog>
  );
};

export default HouseholdCompositionModal;
